import React, { useEffect, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import client from '../../api/client';
import { CheckCircle2, Circle, Clock, Loader2, Sparkles, RefreshCw } from 'lucide-react';

const statusIcons = {
  done: { Icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  'in-progress': { Icon: Clock, color: 'text-[#7B2C3B]', bg: 'bg-[#F4E4DA]' },
  upcoming: { Icon: Circle, color: 'text-[#B8896C]', bg: 'bg-[#FBF7F2]' },
};

function phaseStatus(p) {
  const total = p.tasks.length;
  const done = p.tasks.filter(t => t.done).length;
  if (done === total) return 'done';
  if (done > 0) return 'in-progress';
  return 'upcoming';
}

export default function Timeline() {
  const [phases, setPhases] = useState([]);
  const [weddingDate, setWeddingDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [toast, setToast] = useState('');

  const load = async () => {
    try {
      const { data } = await client.get('/timeline');
      setPhases(data.phases || []);
      setWeddingDate(data.wedding_date || null);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (phaseIdx, taskIdx) => {
    const next = [...phases];
    next[phaseIdx] = { ...next[phaseIdx], tasks: [...next[phaseIdx].tasks] };
    next[phaseIdx].tasks[taskIdx] = { ...next[phaseIdx].tasks[taskIdx], done: !next[phaseIdx].tasks[taskIdx].done };
    setPhases(next);
    try {
      await client.put('/timeline/task', { phaseIdx, taskIdx, done: next[phaseIdx].tasks[taskIdx].done });
    } catch { /* ignore */ }
  };

  const regenerate = async () => {
    if (regenerating) return;
    if (!weddingDate) {
      setToast('Set a wedding date in Settings first — the AI needs to know your window.');
      setTimeout(() => setToast(''), 4000);
      return;
    }
    if (!window.confirm('Rewrite your timeline for your exact wedding date? Existing task checkmarks are preserved.')) return;
    setRegenerating(true);
    try {
      const { data } = await client.post('/timeline/regenerate');
      setPhases(data.phases || []);
      setToast(data.ai_used ? 'Timeline rebuilt by your AI wedding friend.' : 'Timeline rescaled to your window.');
      setTimeout(() => setToast(''), 4000);
    } catch (e) {
      setToast(e?.response?.data?.detail || 'Regenerate failed. Try again.');
      setTimeout(() => setToast(''), 4000);
    } finally { setRegenerating(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-[#6A6A6A]"><Loader2 className="w-4 h-4 animate-spin" /> Loading timeline…</div>;

  // Nudge if the seeded 12-month labels don't match the window. The
  // dynamic scaler auto-applies on GET, so this is just a friendly hint.
  const emptyState = phases.length === 0;

  return (
    <div className="animate-float-up">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl text-[#2A2A2A] mb-1 flex items-center gap-3"><AppIcon feature="timeline" size="lg" reveal testId="section-icon-timeline" />Timeline</h1>
          <p className="text-[#6A6A6A]">Every milestone, mapped to your date. Never wonder "what's next" again.</p>
          {weddingDate && (
            <p className="text-xs text-[#9A8A7E] mt-1">Anchored to your wedding on {new Date(weddingDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })}</p>
          )}
        </div>
        <button
          data-testid="timeline-regenerate"
          onClick={regenerate}
          disabled={regenerating}
          className="btn-primary text-xs px-4 py-2 rounded-full inline-flex items-center gap-1.5 disabled:opacity-60 flex-shrink-0"
        >
          {regenerating ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {regenerating ? 'Rewriting…' : 'Rewrite with AI for my dates'}
        </button>
      </div>

      {toast && (
        <div className="mb-4 text-sm px-4 py-2 rounded-xl bg-[#F4E4DA] text-[#7B2C3B] border border-[#EBD3C2]" data-testid="timeline-toast">{toast}</div>
      )}

      {emptyState ? (
        <div className="soft-card rounded-2xl p-8 text-center text-[#6A6A6A]">
          <p className="mb-4">Every phase in the seed timeline is already past your wedding date. That's usually because your wedding is a few weeks away — click <strong>Rewrite with AI</strong> to get a plan tuned to the days you have.</p>
          <button onClick={regenerate} disabled={regenerating} className="btn-primary text-sm px-4 py-2 rounded-full inline-flex items-center gap-1.5">
            <Sparkles className="w-4 h-4" /> Rewrite with AI
          </button>
        </div>
      ) : (
        <div className="relative">
          <div className="absolute left-5 top-0 bottom-0 w-px bg-[#EFE3D8]" />
          <div className="space-y-6">
            {phases.map((phase, i) => {
              const status = phaseStatus(phase);
              const cfg = statusIcons[status];
              const Icon = cfg.Icon;
              return (
                <div key={i} className="relative pl-14" data-testid={`timeline-phase-${i}`}>
                  <div className={`absolute left-0 w-10 h-10 rounded-full flex items-center justify-center ${cfg.bg} border-2 border-[#FBF7F2] shadow-sm`}>
                    <Icon className={`w-5 h-5 ${cfg.color}`} />
                  </div>
                  <div className="soft-card rounded-2xl p-5">
                    <div className="flex items-start justify-between mb-3 gap-3">
                      <div className="min-w-0">
                        <h3 className="font-serif text-xl text-[#2A2A2A]">{phase.date || phase.label}</h3>
                        {phase.targetDate && (
                          <p className="text-xs text-[#9A8A7E] mt-0.5">By {new Date(phase.targetDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', timeZone: 'UTC' })}</p>
                        )}
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full border capitalize flex-shrink-0 ${status === 'done' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : status === 'in-progress' ? 'bg-[#F4E4DA] text-[#7B2C3B] border-[#EBD3C2]' : 'bg-stone-100 text-stone-600 border-stone-200'}`}>
                        {status.replace('-', ' ')}
                      </span>
                    </div>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {phase.tasks.map((t, j) => (
                        <li key={j} className="flex items-center gap-2 text-sm cursor-pointer group" onClick={() => toggle(i, j)} data-testid={`timeline-task-${i}-${j}`}>
                          <div className={`w-4 h-4 rounded-md border ${t.done ? 'bg-[#7B2C3B] border-[#7B2C3B]' : 'border-[#B8896C] group-hover:bg-[#F4D6CE]'} flex items-center justify-center transition-colors`}>
                            {t.done && <CheckCircle2 className="w-2.5 h-2.5 text-[#FBF7F2]" />}
                          </div>
                          <span className={`${t.done ? 'line-through text-[#9A8A7E]' : 'text-[#3A3A3A]'}`}>{t.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
